/* eslint-disable max-len */
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import {
  GridCellParams,
  GridColDef,
  GridEditInputCell,
  GridEditSingleSelectCell,
  GridRenderEditCellParams,
  GridRowEditStopParams,
  MuiEvent,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DatePicker from 'react-datepicker';
import { useAppSelector } from '../../../redux/hooks';
import { selectLiteracyScreeningOptionSets } from '../../../redux/slices/literacyScreeningSlice';
import { LiteracyScreeningGradesModel } from '../../../types/LiteracyScreeningGradesModel';
import { LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION } from '../../../types/LiteracyScreeningModel';
import { PanelType } from '../../../types/PanelType';
import { SelectOption } from '../../../types/SelectOption';
import {
  getLiteracyScreeningBoyFieldError,
  validateLiteracyScreeningBoyRow,
} from '../../../types/Validation/LiteracyScreeningScreenerValidationSchema';
import { formatTypedDateInput } from '../../../utilities/dateUtilities';
import Panel from '../../Panel/Panel';
import StyledGrid from '../../StyledGrid/StyledGrid';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';

const NUMERIC_COUNT_REGEX = /^\d+$/;
const SESSION_EDIT_FIELDS = [
  'screener',
  'screenerStartDate',
  'screenerEndDate',
  'studentsAssessed',
  'studentsBelowGradeLevel',
  'studentIntervention',
] as const;

export interface LiteracyScreeningSessionGridRow {
  id: string;
  gradeLevel: string;
  gradeDisplay: string;
  screener: string;
  screenerStartDate: string;
  screenerEndDate: string;
  studentsAssessed: string;
  studentsBelowGradeLevel: string;
  studentIntervention: string;
  isTotal?: boolean;
}

export type LiteracyScreeningBoyGridRow = LiteracyScreeningSessionGridRow;

export interface LiteracyScreeningSessionEditState {
  hasCommittedEdits: boolean;
  pendingEditDirty: boolean;
  pendingEditHasError: boolean;
  rows: LiteracyScreeningSessionGridRow[];
}

export type LiteracyScreeningBoyEditState = LiteracyScreeningSessionEditState;

const formatGradeLabel = (gradeLevel: string): string => {
  const gradeNumber = Number.parseInt(gradeLevel, 10);
  if (Number.isFinite(gradeNumber)) {
    return `Grade ${gradeNumber}`;
  }
  return gradeLevel;
};

const toInputValue = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value);
};

const startOfLocalDay = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const isValidCalendarDateParts = (
  month: number,
  day: number,
  year: number
): boolean => {
  if (year < 1000 || year > 2999) {
    return false;
  }
  if (month < 1 || month > 12) {
    return false;
  }
  const daysInMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
};

const parseDateValue = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfLocalDay(value);
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const datePart = raw.split('T')[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const typedMatch = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(raw);
  if (typedMatch) {
    const month = Number(typedMatch[1]);
    const day = Number(typedMatch[2]);
    const year = Number(typedMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  if (compactMatch) {
    const month = Number(compactMatch[1]);
    const day = Number(compactMatch[2]);
    const year = Number(compactMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  return null;
};

const formatDateValue = (value: unknown): string => {
  const date = parseDateValue(value);
  if (!date) {
    return typeof value === 'string' ? value.trim() : '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDatePickerBounds = (
  schoolYear: string
): { minDate?: Date; maxDate: Date } => {
  const today = startOfLocalDay(new Date());
  const endYear = Number(schoolYear);
  if (!Number.isFinite(endYear) || endYear <= 0) {
    return { maxDate: today };
  }

  const yearEnd = new Date(endYear, 5, 30);
  return {
    minDate: new Date(endYear - 1, 6, 1),
    maxDate: today < yearEnd ? today : yearEnd,
  };
};

const formatDisplayDate = (value: unknown): string => {
  const date = parseDateValue(value);
  if (!date) {
    return '';
  }

  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
    date.getDate()
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

const isDatePickerEventTarget = (event: MuiEvent): boolean => {
  const target =
    event && typeof event === 'object' && 'target' in event
      ? (event.target as HTMLElement | null)
      : null;

  return Boolean(
    target?.closest?.(
      '.react-datepicker-popper, .react-datepicker__portal, .react-datepicker'
    )
  );
};

const StyledErrorTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    placement={props.placement ?? 'bottom'}
    PopperProps={{
      disablePortal: false,
      popperOptions: { strategy: 'fixed' },
      ...props.PopperProps,
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const parseCount = (value: string): number | null => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }

  const trimmed = String(value).trim();
  if (!NUMERIC_COUNT_REGEX.test(trimmed)) {
    return null;
  }

  return Number(trimmed);
};

const NumericEditInputCell = (
  props: GridRenderEditCellParams
): ReactElement => {
  const { error } = props;

  return (
    <StyledErrorTooltip open={!!error} title={error}>
      <GridEditInputCell {...props} />
    </StyledErrorTooltip>
  );
};

const renderNumericEditCell = (
  params: GridRenderEditCellParams
): ReactElement => {
  return <NumericEditInputCell {...params} />;
};

const ScreenerEditInputCell = (
  props: GridRenderEditCellParams
): ReactElement => {
  const { error } = props;

  return (
    <StyledErrorTooltip open={!!error} title={error}>
      <div className="literacy-screening-screener-edit-cell">
        <GridEditSingleSelectCell {...props} />
      </div>
    </StyledErrorTooltip>
  );
};

const renderScreenerEditCell = (
  params: GridRenderEditCellParams
): ReactElement => {
  return <ScreenerEditInputCell {...params} />;
};

const DateEditInputCell = (
  props: GridRenderEditCellParams & {
    minDate?: Date;
    maxDate?: Date;
  }
): ReactElement => {
  const { id, field, value, error, hasFocus, minDate, maxDate } = props;
  const apiRef = useGridApiContext();
  const [isCalendarOpen, setIsCalendarOpen] = useState(hasFocus);
  const [rawInputValue, setRawInputValue] = useState('');
  const selected = parseDateValue(value);

  useEffect(() => {
    if (hasFocus) {
      setIsCalendarOpen(true);
    }
  }, [hasFocus]);

  const commitDateValue = (next: Date | string | null): void => {
    apiRef.current.setEditCellValue({ id, field, value: next });
  };

  const handleChangeRaw = (event?: ChangeEvent<HTMLInputElement>): void => {
    if (!event?.target) {
      return;
    }

    let inputValue = event.target.value || '';
    const formatted = formatTypedDateInput(inputValue);
    if (formatted !== inputValue) {
      event.target.value = formatted;
      inputValue = formatted;
    }
    setRawInputValue(inputValue);

    if (!inputValue) {
      commitDateValue(null);
      return;
    }

    const parsed = parseDateValue(inputValue);
    if (parsed) {
      setIsCalendarOpen(true);
      commitDateValue(parsed);
      return;
    }

    setIsCalendarOpen(false);
    commitDateValue(inputValue);
  };

  return (
    <StyledErrorTooltip open={!!error && !isCalendarOpen} title={error}>
      <div
        className={`literacy-screening-date-edit-cell${
          error ? ' Mui-error' : ''
        }`}
      >
        <DatePicker
          selected={selected}
          value={rawInputValue.length > 0 ? rawInputValue : undefined}
          onChangeRaw={handleChangeRaw}
          onChange={(next: Date | null) => {
            setRawInputValue('');
            commitDateValue(next);
            setIsCalendarOpen(false);
          }}
          dateFormat="MM/dd/yyyy"
          placeholderText="mm/dd/yyyy"
          className={`literacy-screening-date-input${
            error ? ' Mui-error' : ''
          }`}
          wrapperClassName="literacy-screening-date-wrapper"
          autoFocus={hasFocus}
          open={isCalendarOpen}
          onInputClick={() => setIsCalendarOpen(true)}
          onFocus={() => setIsCalendarOpen(true)}
          onClickOutside={() => setIsCalendarOpen(false)}
          onCalendarClose={() => setIsCalendarOpen(false)}
          minDate={minDate}
          maxDate={maxDate}
          portalId="literacy-screening-datepicker-portal"
          popperPlacement="bottom-start"
          popperProps={{ strategy: 'fixed' }}
          showPopperArrow={false}
          enableTabLoop={false}
        />
      </div>
    </StyledErrorTooltip>
  );
};

const getScreenerOptions = (
  optionSets: Record<string, SelectOption[]>
): SelectOption[] => {
  return (optionSets.ScreenerInvoiceOptions || []).filter(
    (option) => !option.isDisabled
  );
};

const sumField = (
  rows: LiteracyScreeningSessionGridRow[],
  field: keyof Pick<
    LiteracyScreeningSessionGridRow,
    'studentsAssessed' | 'studentsBelowGradeLevel' | 'studentIntervention'
  >
): string => {
  let hasValue = false;
  const total = rows.reduce((sum, row) => {
    const parsed = parseCount(row[field]);
    if (parsed === null) {
      return sum;
    }
    hasValue = true;
    return sum + parsed;
  }, 0);
  return hasValue ? String(total) : '';
};

export const calcInterventionPercentage = (
  studentsAssessed: string,
  studentIntervention: string
): string => {
  const assessed = parseCount(studentsAssessed);
  if (assessed === null) {
    return '';
  }

  if (assessed === 0) {
    return '0%';
  }

  const intervention = parseCount(studentIntervention);
  if (intervention === null) {
    return '';
  }

  return `${Math.round((intervention / assessed) * 100)}%`;
};

export const buildSessionGridRows = (
  grades: LiteracyScreeningGradesModel[],
  sessionName: string
): LiteracyScreeningSessionGridRow[] => {
  return (grades || []).map((grade) => {
    const matchingSession = (grade.screenerSession || []).find(
      (session) =>
        (session.session || '').toLowerCase() === sessionName.toLowerCase()
    );

    return {
      id: grade.gradeLevel,
      gradeLevel: grade.gradeLevel,
      gradeDisplay: formatGradeLabel(grade.gradeLevel),
      screener: grade.screener || matchingSession?.screener || '',
      screenerStartDate: toInputValue(matchingSession?.screenerStartDate),
      screenerEndDate: toInputValue(matchingSession?.screenerEndDate),
      studentsAssessed: toInputValue(matchingSession?.studentsAssessed),
      studentsBelowGradeLevel: toInputValue(
        matchingSession?.studentsBelowGradeLevel
      ),
      studentIntervention: toInputValue(matchingSession?.studentIntervention),
    };
  });
};

const REQUIRED_HEADER_CLASS = 'literacy-screening-required-header';

export interface LiteracyScreeningSessionPanelProps {
  grades: LiteracyScreeningGradesModel[];
  schoolYear: string;
  heading: string;
  session: string;
  schoolName?: string;
  disabled?: boolean;
  resetKey?: number;
  onEditStateChange?: (state: LiteracyScreeningSessionEditState) => void;
}

const comparableSessionValue = (field: string, value: unknown): string => {
  if (field === 'screenerStartDate' || field === 'screenerEndDate') {
    return formatDateValue(value);
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

const areSessionRowsEqual = (
  left: LiteracyScreeningSessionGridRow[],
  right: LiteracyScreeningSessionGridRow[]
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((row, index) => {
    const other = right[index];
    return (
      row.gradeLevel === other.gradeLevel &&
      SESSION_EDIT_FIELDS.every(
        (field) =>
          comparableSessionValue(field, row[field]) ===
          comparableSessionValue(field, other[field])
      )
    );
  });
};

const LiteracyScreeningSessionPanel = ({
  grades,
  schoolYear,
  heading,
  session,
  schoolName,
  disabled = false,
  resetKey = 0,
  onEditStateChange,
}: LiteracyScreeningSessionPanelProps): ReactElement => {
  const apiRef = useGridApiRef();
  const optionSets = useAppSelector(selectLiteracyScreeningOptionSets);
  const screenerOptions = getScreenerOptions(optionSets);
  const isScreenerUsedEditable =
    session === LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION;
  const [rows, setRows] = useState<LiteracyScreeningSessionGridRow[]>(() =>
    buildSessionGridRows(grades, session)
  );
  const [pendingEditDirty, setPendingEditDirty] = useState(false);
  const [pendingEditHasError, setPendingEditHasError] = useState(false);
  const lastEditRowsSignatureRef = useRef<string | null>(null);

  const stopOpenRowEdits = useCallback((): void => {
    const editRows = apiRef.current?.state?.editRows ?? {};
    Object.keys(editRows).forEach((rowId) => {
      try {
        apiRef.current.stopRowEditMode({
          id: rowId,
          ignoreModifications: true,
        });
      } catch {
        // Row may already have left edit mode.
      }
    });
  }, [apiRef]);

  useEffect(() => {
    stopOpenRowEdits();
    lastEditRowsSignatureRef.current = null;
    setRows(buildSessionGridRows(grades, session));
    setPendingEditDirty(false);
    setPendingEditHasError(false);
  }, [grades, session, resetKey, stopOpenRowEdits]);

  useEffect(() => {
    if (disabled) {
      stopOpenRowEdits();
      setPendingEditDirty(false);
      setPendingEditHasError(false);
    }
  }, [disabled, stopOpenRowEdits]);

  const syncEditButtonState = useCallback((): void => {
    const api = apiRef.current;
    if (!api?.state) {
      return;
    }

    const editRows = (api.state.editRows ?? {}) as Record<
      string,
      Record<string, { value?: unknown; error?: string | null }>
    >;
    const editRowsSignature = JSON.stringify(editRows);
    if (editRowsSignature === lastEditRowsSignatureRef.current) {
      return;
    }
    lastEditRowsSignatureRef.current = editRowsSignature;

    const rowIds = Object.keys(editRows);
    if (rowIds.length === 0) {
      setPendingEditDirty(false);
      setPendingEditHasError(false);
      return;
    }

    let anyDirty = false;
    let anyError = false;

    for (const rowId of rowIds) {
      const rowFields = editRows[rowId];
      if (!rowFields) {
        continue;
      }

      if (Object.values(rowFields).some((cell) => Boolean(cell?.error))) {
        anyError = true;
      }

      const baseRow = rows.find((row) => String(row.id) === String(rowId));
      if (!baseRow) {
        continue;
      }

      for (const field of SESSION_EDIT_FIELDS) {
        const cell = rowFields[field];
        const baseVal = baseRow[field];
        const editedVal =
          cell !== undefined && cell.value !== undefined ? cell.value : baseVal;
        if (
          comparableSessionValue(field, editedVal) !==
          comparableSessionValue(field, baseVal)
        ) {
          anyDirty = true;
          break;
        }
      }
    }

    setPendingEditDirty((prev) => (prev === anyDirty ? prev : anyDirty));
    setPendingEditHasError((prev) => (prev === anyError ? prev : anyError));
  }, [apiRef, rows]);

  useEffect(() => {
    lastEditRowsSignatureRef.current = null;
    syncEditButtonState();
  }, [syncEditButtonState]);

  useEffect(() => {
    onEditStateChange?.({
      hasCommittedEdits: !areSessionRowsEqual(
        buildSessionGridRows(grades, session),
        rows
      ),
      pendingEditDirty,
      pendingEditHasError,
      rows,
    });
  }, [
    grades,
    session,
    rows,
    pendingEditDirty,
    pendingEditHasError,
    onEditStateChange,
  ]);

  const totalRow = useMemo<LiteracyScreeningSessionGridRow>(
    () => ({
      id: 'total',
      gradeLevel: '',
      gradeDisplay: 'Total',
      screener: '',
      screenerStartDate: '',
      screenerEndDate: '',
      studentsAssessed: sumField(rows, 'studentsAssessed'),
      studentsBelowGradeLevel: sumField(rows, 'studentsBelowGradeLevel'),
      studentIntervention: sumField(rows, 'studentIntervention'),
      isTotal: true,
    }),
    [rows]
  );

  const processRowUpdate = (
    newRow: LiteracyScreeningSessionGridRow
  ): LiteracyScreeningSessionGridRow => {
    const rowError = validateLiteracyScreeningBoyRow(newRow, schoolYear);
    if (rowError) {
      throw new Error(rowError);
    }

    setRows((currentRows) =>
      currentRows.map((row) => (row.id === newRow.id ? newRow : row))
    );
    return newRow;
  };

  const handleCellClick = (
    params: GridCellParams<LiteracyScreeningSessionGridRow>
  ): void => {
    if (!params.isEditable || params.row.isTotal) {
      return;
    }

    if (apiRef.current.getRowMode(params.id) === 'edit') {
      return;
    }

    apiRef.current.startRowEditMode({ id: params.id });
  };

  const handleRowEditStop = (
    _params: GridRowEditStopParams,
    event: MuiEvent
  ): void => {
    if (isDatePickerEventTarget(event)) {
      event.defaultMuiPrevented = true;
    }
  };

  const datePickerBounds = useMemo(
    () => getDatePickerBounds(schoolYear),
    [schoolYear]
  );

  const columns: GridColDef<LiteracyScreeningSessionGridRow>[] = useMemo(
    () => [
      {
        field: 'gradeDisplay',
        headerName: 'Grade',
        width: 88,
        minWidth: 88,
        maxWidth: 88,
        sortable: false,
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'screener',
        headerName: 'Screener Used',
        headerClassName: isScreenerUsedEditable ? REQUIRED_HEADER_CLASS : '',
        type: 'singleSelect',
        editable: isScreenerUsedEditable,
        flex: 0.7,
        minWidth: 150,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        cellClassName: (params) =>
          isScreenerUsedEditable || params.row.isTotal
            ? ''
            : 'literacy-screening-screener-disabled-cell',
        valueOptions: screenerOptions.map((option) => ({
          value: option.value,
          label: option.text,
        })),
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: getLiteracyScreeningBoyFieldError(
            'screener',
            { ...params.row, screener: params.props.value },
            schoolYear
          ),
        }),
        renderEditCell: renderScreenerEditCell,
      },
      {
        field: 'screenerStartDate',
        headerName: 'Screener Start Date',
        headerClassName: REQUIRED_HEADER_CLASS,
        type: 'date',
        editable: true,
        flex: 1,
        minWidth: 120,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) =>
          parseDateValue(row.screenerStartDate) ?? row.screenerStartDate ?? '',
        valueSetter: (value, row) => ({
          ...row,
          screenerStartDate: formatDateValue(value),
        }),
        valueFormatter: (value) =>
          formatDisplayDate(value) || (typeof value === 'string' ? value : ''),
        preProcessEditCellProps: (params) => {
          const endValue =
            params.otherFieldsProps?.screenerEndDate?.value ??
            params.row.screenerEndDate;
          return {
            ...params.props,
            error: getLiteracyScreeningBoyFieldError(
              'screenerStartDate',
              {
                ...params.row,
                screenerStartDate: params.props.value,
                screenerEndDate: endValue,
              },
              schoolYear
            ),
          };
        },
        renderEditCell: (params) => (
          <DateEditInputCell {...params} {...datePickerBounds} />
        ),
      },
      {
        field: 'screenerEndDate',
        headerName: 'Screener End Date',
        headerClassName: REQUIRED_HEADER_CLASS,
        type: 'date',
        editable: true,
        flex: 1,
        minWidth: 120,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) =>
          parseDateValue(row.screenerEndDate) ?? row.screenerEndDate ?? '',
        valueSetter: (value, row) => ({
          ...row,
          screenerEndDate: formatDateValue(value),
        }),
        valueFormatter: (value) =>
          formatDisplayDate(value) || (typeof value === 'string' ? value : ''),
        preProcessEditCellProps: (params) => {
          const startValue =
            params.otherFieldsProps?.screenerStartDate?.value ??
            params.row.screenerStartDate;
          return {
            ...params.props,
            error: getLiteracyScreeningBoyFieldError(
              'screenerEndDate',
              {
                ...params.row,
                screenerStartDate: startValue,
                screenerEndDate: params.props.value,
              },
              schoolYear
            ),
          };
        },
        renderEditCell: (params) => (
          <DateEditInputCell {...params} {...datePickerBounds} />
        ),
      },
      {
        field: 'studentsAssessed',
        headerName: 'Number of Students Given the Screener',
        headerClassName: REQUIRED_HEADER_CLASS,
        type: 'string',
        editable: true,
        flex: 1,
        minWidth: 140,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: getLiteracyScreeningBoyFieldError(
            'studentsAssessed',
            { ...params.row, studentsAssessed: params.props.value },
            schoolYear
          ),
        }),
        renderEditCell: renderNumericEditCell,
      },
      {
        field: 'studentsBelowGradeLevel',
        headerName:
          'Number of Students Flagged Below Grade Level on Screener and Given Diagnostic',
        headerClassName: REQUIRED_HEADER_CLASS,
        type: 'string',
        editable: true,
        flex: 1.3,
        minWidth: 170,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: getLiteracyScreeningBoyFieldError(
            'studentsBelowGradeLevel',
            { ...params.row, studentsBelowGradeLevel: params.props.value },
            schoolYear
          ),
        }),
        renderEditCell: renderNumericEditCell,
      },
      {
        field: 'studentIntervention',
        headerName:
          'Number of Students Identified for Intervention from the Diagnostic',
        headerClassName: REQUIRED_HEADER_CLASS,
        type: 'string',
        editable: true,
        flex: 1.3,
        minWidth: 170,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: getLiteracyScreeningBoyFieldError(
            'studentIntervention',
            { ...params.row, studentIntervention: params.props.value },
            schoolYear
          ),
        }),
        renderEditCell: renderNumericEditCell,
      },
      {
        field: 'interventionPercentage',
        headerName: 'Percentage of Students Identified for Intervention',
        editable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) => {
          const sourceRow = row.isTotal
            ? row
            : (apiRef.current.getRowWithUpdatedValues(
                row.id,
                'studentIntervention'
              ) as LiteracyScreeningSessionGridRow);

          return calcInterventionPercentage(
            sourceRow.studentsAssessed,
            sourceRow.studentIntervention
          );
        },
      },
    ],
    [
      apiRef,
      datePickerBounds,
      isScreenerUsedEditable,
      schoolYear,
      screenerOptions,
    ]
  );

  return (
    <Panel
      panelType={PanelType.INFO}
      heading={heading}
      className={`literacy-screening-session-panel${
        disabled ? ' literacy-screening-session-panel--disabled' : ''
      }`}
    >
      {schoolName !== undefined && (
        <div className="field-container disabled literacy-screening-school-name">
          <FormInputLabel
            field="literacy-screening-school-name"
            displayName="School Name"
            disabled
          />
          <input
            id="literacy-screening-school-name"
            className="data-input"
            type="text"
            value={schoolName}
            disabled
            readOnly
          />
        </div>
      )}
      <div className="literacy-screening-session-grid">
        <StyledGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          editMode="row"
          processRowUpdate={processRowUpdate}
          onCellClick={handleCellClick}
          onRowEditStop={handleRowEditStop}
          onStateChange={syncEditButtonState}
          onProcessRowUpdateError={() => undefined}
          isCellEditable={(params) =>
            !disabled && Boolean(params.colDef.editable) && !params.row.isTotal
          }
          autoHeight
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableRowSelectionOnClick
          columnHeaderHeight={72}
          pinnedRows={{ bottom: [totalRow] }}
          getRowClassName={(params) =>
            params.row.isTotal ? 'literacy-screening-total-row' : ''
          }
        />
      </div>
    </Panel>
  );
};

export default LiteracyScreeningSessionPanel;
